package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.MarkNotificationsReadRequest
import fr.frux.shared.dto.NotificationListResponse
import fr.frux.shared.dto.NotificationPreferencesDto
import fr.frux.shared.dto.PushPlatformDto
import fr.frux.shared.dto.RegisterPushTokenRequest
import fr.frux.shared.dto.RevokePushTokenRequest
import fr.frux.shared.dto.UpdateNotificationPreferencesRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody

/**
 * API client pour le socle de notifications (#225 P0, ADR 0017). Miroir de
 * `fr.frux.api.routes.notificationRoutes` côté serveur.
 */
interface NotificationsApi {
    /**
     * Page de notifications, non-lues d'abord côté serveur. [beforeId] est le curseur reçu en
     * `nextCursor` de la page précédente ; `null` demande la première page.
     */
    suspend fun getNotifications(
        beforeId: Long? = null,
        limit: Int = 20,
    ): NotificationListResponse

    /** Marque les notifications [ids] comme lues (no-op silencieux sur ids inconnus/déjà lus). */
    suspend fun markRead(ids: List<Long>)

    /** Marque toutes les notifications de l'utilisateur comme lues. */
    suspend fun markAllRead()

    /** Préférences de notification de l'utilisateur courant. */
    suspend fun getPreferences(): NotificationPreferencesDto

    /**
     * Met à jour les préférences. `inAppEnabled` de [request] est ignoré silencieusement côté
     * serveur (D9, ADR 0017) — le contrat le renvoie toujours à `true`.
     */
    suspend fun updatePreferences(request: UpdateNotificationPreferencesRequest): NotificationPreferencesDto

    /**
     * Enregistre (ou réattribue) le jeton d'appareil de l'utilisateur courant (#252 P2, ADR 0017).
     * Idempotent : appelé à chaque `onNewToken` du fournisseur et à chaque activation du toggle push.
     */
    suspend fun registerPushToken(
        token: String,
        platform: PushPlatformDto,
    )

    /** Révoque [token] côté serveur (déconnexion). Idempotent, y compris sur un jeton inconnu. */
    suspend fun revokePushToken(token: String)
}

/**
 * Implementation of NotificationsApi using Ktor HttpClient.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class NotificationsApiImpl(
    private val httpClient: HttpClient,
) : NotificationsApi {
    override suspend fun getNotifications(
        beforeId: Long?,
        limit: Int,
    ): NotificationListResponse =
        httpClient
            .get("${ApiPaths.V1}/notifications") {
                parameter("limit", limit)
                beforeId?.let { parameter("beforeId", it) }
            }.body()

    override suspend fun markRead(ids: List<Long>) {
        httpClient.post("${ApiPaths.V1}/notifications/read") {
            setBody(MarkNotificationsReadRequest(ids = ids))
        }
    }

    override suspend fun markAllRead() {
        httpClient.post("${ApiPaths.V1}/notifications/read") {
            setBody(MarkNotificationsReadRequest(all = true))
        }
    }

    override suspend fun getPreferences(): NotificationPreferencesDto =
        httpClient
            .get("${ApiPaths.V1}/me/notification-preferences")
            .body()

    override suspend fun updatePreferences(request: UpdateNotificationPreferencesRequest): NotificationPreferencesDto =
        httpClient
            .put("${ApiPaths.V1}/me/notification-preferences") {
                setBody(request)
            }.body()

    override suspend fun registerPushToken(
        token: String,
        platform: PushPlatformDto,
    ) {
        httpClient.post("${ApiPaths.V1}/me/push-tokens") {
            setBody(RegisterPushTokenRequest(token = token, platform = platform))
        }
    }

    override suspend fun revokePushToken(token: String) {
        httpClient.delete("${ApiPaths.V1}/me/push-tokens") {
            setBody(RevokePushTokenRequest(token = token))
        }
    }
}
